import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

import Layout from './components/Layout/Layout';
import LoadingScreen from './components/UI/LoadingScreen';
import Box from '@mui/material/Box';

const Login = lazy(() => import('./components/Auth/Login'));
const Shop = lazy(() => import('./pages/Shop'));
const Inventory = lazy(() => import('./pages/Inventory'));
const PrintDocument = lazy(() => import('./pages/print/PrintDocument'));
const PrintIspDocument = lazy(() => import('./pages/print/PrintIspDocument'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading } = useAuth();
  if (loading) {
    return <LoadingScreen />;
  }
  return token ? children : <Navigate to="/login" replace />;
};

const getBackendOrigin = () => {
  const hasExplicitApiBase = !!process.env.REACT_APP_API_URL;
  if (!hasExplicitApiBase && typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    // Local dev default: frontend on 56001 proxies backend on 56000.
    if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && String(window.location.port || '') === '56001') {
      return `${window.location.protocol}//${window.location.hostname}:56000`;
    }
  }

  const apiBase = process.env.REACT_APP_API_URL
    || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api');
  try {
    const parsed = new URL(apiBase);
    return `${parsed.protocol}//${parsed.host}`;
  } catch (_) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }
};

const isSameLocationTarget = (target) => {
  if (typeof window === 'undefined') return false;
  if (!target) return true;
  try {
    const current = new URL(window.location.href);
    const next = new URL(target, window.location.origin);
    const normalize = (url) => `${url.origin}${String(url.pathname || '').replace(/\/+$/, '') || '/'}`;
    return normalize(current) === normalize(next);
  } catch (_) {
    return false;
  }
};

const InitializationRedirect = () => {
  useEffect(() => {
    const backendOrigin = getBackendOrigin();
    const target = backendOrigin ? `${backendOrigin}/initialization` : '/initialization';
    if (isSameLocationTarget(target)) return;
    window.location.replace(target);
  }, []);

  return <LoadingScreen />;
};

const LicenseRedirect = () => {
  useEffect(() => {
    const backendOrigin = getBackendOrigin();
    const target = backendOrigin ? `${backendOrigin}/license` : '/license';
    if (isSameLocationTarget(target)) return;
    window.location.replace(target);
  }, []);

  return <LoadingScreen />;
};

function App() {
  const { token, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          <Route path="/login" element={token ? <Navigate to="/" replace /> : <Login />} />
          <Route path="/initialization" element={<InitializationRedirect />} />
          <Route path="/license" element={<LicenseRedirect />} />
          <Route path="/renew/:token" element={<Navigate to="/shop" replace />} />
          <Route path="/print/:docType/:id" element={<ProtectedRoute><PrintDocument /></ProtectedRoute>} />
          <Route path="/print/isp/:docType/:id" element={<ProtectedRoute><PrintIspDocument /></ProtectedRoute>} />
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/shop" replace />} />
            <Route path="customers/*" element={<Navigate to="/shop?module=sales" replace />} />
            <Route path="packages/*" element={<Navigate to="/shop?module=settings&section=shop" replace />} />
            <Route path="packages-and-parents" element={<Navigate to="/shop?module=settings&section=shop" replace />} />
            <Route path="billing/*" element={<Navigate to="/shop?module=sales" replace />} />
            <Route path="payments/*" element={<Navigate to="/shop?module=sales" replace />} />
            <Route path="credit-ledger" element={<Navigate to="/shop?module=banking" replace />} />
            <Route path="support/*" element={<Navigate to="/shop?module=support" replace />} />
            <Route path="network/*" element={<Navigate to="/shop?module=settings&section=shop" replace />} />
            <Route path="reports/*" element={<Navigate to="/shop?module=sales" replace />} />
            <Route path="settings/*" element={<Navigate to="/shop?module=settings&section=company" replace />} />
            <Route path="shop" element={<Shop />} />
            <Route path="inventory/*" element={<Inventory />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </Box>
  );
}

export default App;
